import React, { Suspense, lazy, useState, useEffect } from "react";
import { BrowserRouter as Router, Route } from "react-router-dom";

import "semantic-ui-css/semantic.min.css";
import LoginPage from "./login";
import { AuthProvider } from "./shared/context/auth";
import { verifyCookies, doCookiesExist } from "./shared/utilities/auth";
import { PageView } from "./analytics";
import ga from "react-ga";
ga.initialize("UA-140240784-1", {
  titleCase: false
});

const HomePage = lazy(() => import("./instances/home"));
const ECommContainer = lazy(() => import("./instances/ecommerce/index"));
const Dev = lazy(() => import("./instances/dev"));
const MobileContainer = lazy(() => import("./instances/mobile"));
const SimplePlayer = lazy(() => import("./instances/simple"));

// Demos
const NI = lazy(() => import("./demos/ni"));
const GardnerDenver = lazy(() => import("./demos/GardnerDenver"));

// Admin Routes
const AdminHeader = lazy(() => import("./instances/admin/header"));
const AdminContainer = lazy(() => import("./instances/admin"));

function PrivateRoute({ component: Component, access, ...rest }) {
  return (
    <Route
      render={props => {
        if (access) {
          PageView();
          return <Component {...props} {...rest} />;
        } else {
          PageView("/login");
          return <LoginPage {...props} {...rest} />;
        }
      }}
      {...rest}
    />
  );
}

const AdminRoute = ({ component: Component, profile, authorize, ...rest }) => (
  <Route
    render={function(props) {
      if (profile === "admin") {
        PageView();
        return <Component {...props} {...rest} />;
      } else {
        PageView("/admin/login");
        return <LoginPage {...props} {...rest} authorize={authorize} />;
      }
    }}
    {...rest}
  />
);

const App = () => {
  const [auth, setAuth] = useState({
    verified: false,
    access: doCookiesExist(),
    profile: null
  });

  const authorize = args => setAuth(args);

  //Verify the username & password are valid
  if (!auth.verified && auth.access) {
    verifyCookies().then(function({ _access, _profile }) {
      setAuth({
        ...auth,
        ...{
          access: _access,
          profile: _profile,
          verified: true,
          setAuth: props => setAuth(props)
        }
      });
    });
  }

  const _auth = { ...auth };

  return (
    <Router>
      <Suspense fallback={null}>
        <AuthProvider value={{ ...auth }}>
          <PrivateRoute
            exact
            path="/"
            component={HomePage}
            authorize={authorize}
            {..._auth}
          />
          <PrivateRoute
            path="/develop"
            component={Dev}
            authorize={authorize}
            {..._auth}
          />
          <PrivateRoute
            path="/ecom/:id"
            component={ECommContainer}
            authorize={authorize}
            {..._auth}
          />
          <PrivateRoute
            path="/mobile/:id"
            component={MobileContainer}
            authorize={authorize}
            {...auth}
          />
          <PrivateRoute
            path="/player/:sceneId"
            component={SimplePlayer}
            authorize={authorize}
            {...auth}
          />

          <PrivateRoute path="/demo/ni" component={NI} {..._auth} />
          <PrivateRoute path="/demo/gd" component={GardnerDenver} {..._auth} />

          <AdminRoute
            path="/admin"
            component={AdminHeader}
            authorize={authorize}
            {...auth}
          />

          <AdminRoute
            path="/admin/:obj/:page/:id?"
            component={AdminContainer}
            authorize={authorize}
            {..._auth}
          />
        </AuthProvider>
      </Suspense>
    </Router>
  );
};

export default App;
